// Selector de concepto del catálogo de Finanzas (7-oct-2026, Mabel): un <select> nativo agrupado por
// «Apartado · Categoría» (en celular el nativo es el que mejor se usa). Lo comparten el gasto, el gasto
// programado y el cobro «Otro» (ese solo con Ingresos).
import { useCallback, useEffect, useState } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { getCatalog, createFinCategory, createFinConcept, FinCatalog, FinSection } from '../api';
import { useToast } from '../../../context/ToastContext';

let cache: { data: FinCatalog[]; iva: number; fijo: boolean; at: number } | null = null;
/** Catálogo con caché de 1 min (se pide en cada modal). `invalidarCatalogo()` tras editarlo. */
export function useCatalogo() {
  const [cat, setCat] = useState<FinCatalog[] | null>(cache?.data || null);
  const [iva, setIva] = useState<number>(cache?.iva || 16);
  // ¿La API ya guarda Fijo/Variable? (hasta reiniciar lealify-base no: entonces la casilla no sale).
  const [fijo, setFijo] = useState<boolean>(!!cache?.fijo);
  const cargar = useCallback((forzar = false) => {
    if (!forzar && cache && Date.now() - cache.at < 60000) return Promise.resolve();
    return getCatalog().then((r) => { cache = { data: r.data, iva: r.iva_default || 16, fijo: !!r.gasto_fijo, at: Date.now() }; setCat(r.data); setIva(cache.iva); setFijo(cache.fijo); }).catch(() => setCat((c) => c || []));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  /** Vuelve a pedir el catálogo (tras dar de alta una categoría o concepto desde un formulario). */
  const recargar = useCallback(() => { cache = null; return cargar(true); }, [cargar]);
  return { catalogo: cat, ivaDefault: iva, gastoFijo: fijo, recargar };
}
export function invalidarCatalogo() { cache = null; }

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

export default function ConceptoSelect({ value, onChange, secciones, placeholder = 'Elige el concepto…', className }: {
  value: number | null;
  onChange: (conceptId: number | null, etiqueta: string) => void;
  secciones: FinSection[];
  placeholder?: string;
  className?: string;
}) {
  const { catalogo } = useCatalogo();
  const lista = (catalogo || []).filter((s) => secciones.includes(s.section));
  return (
    <select className={className || inputCls} value={value ?? ''} disabled={!catalogo}
      onChange={(e) => {
        const id = e.target.value ? Number(e.target.value) : null;
        let et = '';
        for (const s of lista) for (const c of s.categorias) for (const k of c.conceptos) if (k.concept_id === id) et = `${c.name} · ${k.name}`;
        onChange(id, et);
      }}>
      <option value="">{catalogo ? placeholder : 'Cargando…'}</option>
      {lista.map((s) => s.categorias.filter((c) => c.conceptos.length).map((c) => (
        <optgroup key={c.category_id} label={secciones.length > 1 ? `${s.label} · ${c.name}` : c.name}>
          {c.conceptos.map((k) => <option key={k.concept_id} value={k.concept_id}>{k.name}</option>)}
        </optgroup>
      )))}
    </select>
  );
}

/** Renglón «+ Nueva …»: input + palomita, Esc/✕ cancela. */
function AltaEnLinea({ placeholder, onAdd, onCancel }: { placeholder: string; onAdd: (nombre: string) => Promise<void>; onCancel: () => void }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const ok = async () => {
    if (!v.trim() || busy) return;
    setBusy(true);
    try { await onAdd(v.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className={inputCls}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } if (e.key === 'Escape') onCancel(); }} />
      <button type="button" onClick={ok} disabled={busy || !v.trim()} title="Guardar"
        className="shrink-0 rounded-lg bg-accent-solid p-2 text-on-accent disabled:opacity-50"><Check size={15} /></button>
      <button type="button" onClick={onCancel} title="Cancelar" className="shrink-0 rounded-lg p-2 text-content-3 hover:text-content"><X size={15} /></button>
    </div>
  );
}

/**
 * Categoría › Concepto en DOS selects, con alta en línea de cada uno (Mabel 7-oct-2026, al ver la ficha
 * de gasto de Kydemy: «aquí irían las categorías» y «ocupo la opción de poder dar de alta categorías y
 * conceptos»). `secciones` = de qué apartados se ofrecen categorías; la nueva categoría cae en `secciones[0]`.
 * Lo que se da de alta aquí es lo mismo que en Gastos › Categorías y conceptos (mismo catálogo).
 */
export function CategoriaConceptoSelect({ value, onChange, secciones, puedeAlta = true, excluirKeys = [] }: {
  value: number | null;
  onChange: (conceptId: number | null) => void;
  secciones: FinSection[];
  puedeAlta?: boolean;
  /** Conceptos que NO se ofrecen (por system_key); la categoría que se queda sin conceptos tampoco sale.
   *  «Registrar ingreso» quita los que nacen solos de un cobro (mensualidad, inscripción…). */
  excluirKeys?: string[];
}) {
  const toast = useToast();
  const { catalogo, recargar } = useCatalogo();
  const [catId, setCatId] = useState<number | null>(null);
  const [alta, setAlta] = useState<'cat' | 'con' | null>(null);
  const cats = (catalogo || []).filter((s) => secciones.includes(s.section))
    .flatMap((s) => s.categorias.map((c) => ({ ...c, etiqueta: secciones.length > 1 ? `${s.label} · ${c.name}` : c.name,
      conceptos: c.conceptos.filter((k) => !k.system_key || !excluirKeys.includes(k.system_key)) })))
    .filter((c) => c.conceptos.length > 0 || !excluirKeys.length || !c.system_key);
  // Si llega un concepto ya elegido (o se acaba de crear), su categoría se selecciona sola.
  useEffect(() => {
    if (value == null) return;
    const c = cats.find((x) => x.conceptos.some((k) => k.concept_id === value));
    if (c && c.category_id !== catId) setCatId(c.category_id);
  }, [value, catalogo]); // eslint-disable-line react-hooks/exhaustive-deps
  const cat = cats.find((c) => c.category_id === catId) || null;
  // Si la categoría tiene un solo concepto, se elige solo.
  useEffect(() => {
    if (cat && cat.conceptos.length === 1 && value !== cat.conceptos[0].concept_id) onChange(cat.conceptos[0].concept_id);
  }, [catId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function nuevaCategoria(nombre: string) {
    try {
      const r = await createFinCategory(secciones[0], nombre);
      await recargar();
      setCatId(r.category_id); onChange(null); setAlta('con');
      toast.success(`Categoría «${nombre}» creada · ahora agrégale un concepto`);
    } catch (e: any) { toast.error(e?.message || 'No se pudo crear la categoría'); }
  }
  async function nuevoConcepto(nombre: string) {
    if (!catId) return;
    try {
      const r = await createFinConcept(catId, nombre);
      await recargar();
      onChange(r.concept_id); setAlta(null);
      toast.success(`Concepto «${nombre}» creado`);
    } catch (e: any) { toast.error(e?.message || 'No se pudo crear el concepto'); }
  }

  const linkCls = 'mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline';
  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Categoría</label>
        <select className={inputCls} value={catId ?? ''} disabled={!catalogo}
          onChange={(e) => { setCatId(e.target.value ? Number(e.target.value) : null); onChange(null); setAlta(null); }}>
          <option value="">{catalogo ? 'Elige la categoría…' : 'Cargando…'}</option>
          {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.etiqueta}</option>)}
        </select>
        {puedeAlta && (alta === 'cat'
          ? <AltaEnLinea placeholder="Nombre de la categoría" onAdd={nuevaCategoria} onCancel={() => setAlta(null)} />
          : <button type="button" onClick={() => setAlta('cat')} className={linkCls}><Plus size={12} /> Nueva categoría</button>)}
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Concepto</label>
        <select className={inputCls} value={value ?? ''} disabled={!cat}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
          <option value="">{cat ? (cat.conceptos.length ? 'Elige el concepto…' : 'Sin conceptos: agrega uno') : 'Primero la categoría'}</option>
          {(cat?.conceptos || []).map((k) => <option key={k.concept_id} value={k.concept_id}>{k.name}</option>)}
        </select>
        {puedeAlta && cat && (alta === 'con'
          ? <AltaEnLinea placeholder={`Nuevo concepto en ${cat.name}`} onAdd={nuevoConcepto} onCancel={() => setAlta(null)} />
          : <button type="button" onClick={() => setAlta('con')} className={linkCls}><Plus size={12} /> Nuevo concepto</button>)}
      </div>
    </div>
  );
}
